import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  cardClasses,
  cn,
  Icon,
  linkTextClasses,
  NoticeBanner,
  NotProvided,
  StatCard,
  StatCardList,
  Table,
  NotFoundError,
  isNotFound,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  businessDate,
  formatDate,
  ticketStatusLabel,
  ticketStatusTone,
} from '@evogard/domain';
import type { AgendaMemberDetail } from '@evogard/contract';
import { Age, MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { plainText } from '../../lib/plain-text.js';
import { useSessionContext } from '../../lib/session.js';
import { refusalOf } from './common.js';

export type MeasuredClient = Extract<AgendaMemberDetail, { estado: 'medido' }>;
type Vehicle = MeasuredClient['vehicles'][number];
type Boleto = MeasuredClient['boletos'][number];
type ServiceRecord = MeasuredClient['serviceRecords'][number];
type MemberTicket = MeasuredClient['tickets'][number];
type Claim = MeasuredClient['claims'][number];

/** O que se lê do carro aberto. */
export type ClientView = 'boletos' | 'atendimentos';

/**
 * O cliente da agenda — `GET /sales/portfolio/members/:code`. Uma consulta só, e
 * a chave é a mesma da página da ficha: abrir a linha e depois a página não
 * pergunta duas vezes.
 */
export function useAgendaClient(memberCode: string, consultant: string | undefined) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: ['sales', 'agenda-member', tenantId, consultant, memberCode],
    queryFn: async () => {
      const r = await api.sales.agendaMember({
        params: { code: memberCode },
        query: { consultant },
      });
      if (r.status === 404) {
        throw new NotFoundError('Este associado não está na carteira');
      }
      if (r.status !== 200) throw new Error(refusalOf(r, 'o associado da carteira'));
      return r.body;
    },
  });
}

/**
 * O cliente aberto DENTRO da linha da agenda (21/10/2026): a pessoa clica na
 * linha e lê os VEÍCULOS ali mesmo, sem sair da lista — só eles, para a linha
 * aberta não virar uma página dentro da tabela.
 *
 * A LINHA do carro (ou a placa, pelo teclado) leva à página da ficha, já com
 * o carro aberto: o detalhe e os boletos dele pedem espaço, e a página dá a
 * eles o endereço próprio. "Todos os atendimentos" abre os atendimentos ali
 * mesmo, que são uma tabela curta.
 */
export function AgendaClientPanel({
  memberCode,
  consultant,
}: {
  memberCode: string;
  consultant: string | undefined;
}) {
  const query = useAgendaClient(memberCode, consultant);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [view, setView] = useState<ClientView>('boletos');

  /* dentro da linha não cabe o cartão: o 404 é aviso, e não erro */
  if (isNotFound(query.error)) {
    return (
      <Alert tone="alerta">
        {query.error.message}: não há veículo dele com este consultor.
      </Alert>
    );
  }
  if (query.isError) return <Alert tone="erro">{query.error.message}</Alert>;
  if (query.data?.estado === 'sem_vinculo') {
    return <Alert tone="alerta">{query.data.message}</Alert>;
  }
  const measured = query.data as MeasuredClient | undefined;
  const vehicle = measured?.vehicles.find((v) => v.code === selected);
  const ofVehicle = (r: { vehicleCode: string }) =>
    vehicle === undefined || r.vehicleCode === vehicle.code;
  return (
    <div className="flex flex-col gap-4">
      <VehiclesCard
        measured={measured}
        loading={query.isLoading}
        selected={vehicle?.code}
        onOpen={(code, nextView) => {
          if (nextView === 'boletos' && code !== undefined) {
            void navigate({
              to: '/sales/portfolio/members/$code',
              params: { code: memberCode },
              search: { consultant, vehicle: code, view: 'boletos' },
            });
            return;
          }
          setSelected(code);
          setView(nextView);
        }}
      />
      {measured && view === 'atendimentos' && (
        <Attendances
          records={measured.serviceRecords.filter(ofVehicle)}
          tickets={measured.tickets.filter(ofVehicle)}
          vehicle={vehicle}
        />
      )}
    </div>
  );
}

/**
 * A FICHA do cliente, na página própria (`AgendaMemberScreen`). Quem guarda o
 * carro aberto e a aba é o endereço.
 *
 * ## A ordem é a das perguntas de quem atende (21/10/2026)
 *
 * O consultor abre a ficha com o cliente na linha, e as perguntas chegam nesta
 * ordem: o carro está em sinistro? está ativo? o boleto está em dia? — e só
 * depois "quanto vale a FIPE". A página respondia ao contrário: a tabela de um
 * carro só, o cadastro repetido num cartão ao lado, o sinistro no meio e os
 * boletos no pé, abaixo dos atendimentos, mesmo com `view=boletos` no
 * endereço. Agora o sinistro abre a página, os quatro números vêm logo abaixo,
 * boletos e atendimentos dividem uma barra de abas — o `view` passou a dizer
 * qual está aberta —, e o cadastro completo do carro fecha a ficha.
 *
 * ## Só os veículos DESTE consultor
 *
 * Decidido em 29/09/2026: o cliente pode ter carro com outro consultor, e a
 * agenda de um não mostra a carteira do outro. Boletos e atendimentos seguem
 * os veículos pelo mesmo motivo.
 *
 * ## "Pago" é da tabela de situações
 *
 * Um boleto cuja situação o espelho não conhece não é "não pago": é "não
 * sei", e a coluna diz isso.
 */
export function AgendaClientFile({
  measured,
  loading,
  selected,
  view,
  onOpen,
  onView,
}: {
  measured: MeasuredClient | undefined;
  loading: boolean;
  /** O `codigo_veiculo` aberto. Sem ele, a tabela de veículos. */
  selected: string | undefined;
  view: ClientView;
  /** Abre um carro (ou volta a todos, com `undefined`). */
  onOpen: (vehicle: string | undefined) => void;
  /** Troca a aba do carro aberto. */
  onView: (view: ClientView) => void;
}) {
  /*
   * Nenhum carro abre sozinho — nem o do cliente que só tem um (21/10/2026):
   * a tabela de veículos já mostra o que importa, e o resto é escolha. Código
   * que não é carro desta carteira (link velho, carro transferido) abre como
   * nenhum, e não como um carro vazio.
   */
  const vehicle = measured?.vehicles.find((v) => v.code === selected);

  if (loading && selected !== undefined) return <VehicleSummary />;

  if (measured === undefined || vehicle === undefined) {
    return (
      <div className="flex flex-col gap-4">
        {measured && (
          <ClaimsAlert
            claims={measured.claims}
            vehicles={measured.vehicles}
            onOpen={onOpen}
          />
        )}
        <VehiclesCard
          measured={measured}
          loading={loading}
          selected={undefined}
          onOpen={(code) => onOpen(code)}
        />
        {measured && (
          <Attendances
            records={measured.serviceRecords}
            tickets={measured.tickets}
            vehicle={undefined}
          />
        )}
      </div>
    );
  }

  const boletos = measured.boletos.filter((b) => b.vehicleCodes.includes(vehicle.code));
  const records = measured.serviceRecords.filter((r) => r.vehicleCode === vehicle.code);
  const tickets = measured.tickets.filter((t) => t.vehicleCode === vehicle.code);

  /*
   * O carro aberto no desenho da referência do dono (28/10/2026): o cartão do
   * carro, o do sinistro quando há, e as abas de boletos e atendimentos.
   */
  return (
    <div className="flex flex-col gap-4">
      <VehicleHeader
        vehicle={vehicle}
        monthly={boletos[0]?.amount ?? null}
        attendances={records.length + tickets.length}
      />

      {measured.claims
        .filter((c) => c.vehicleCode === vehicle.code)
        .map((c, i) => (
          <ClaimCard key={i} claim={c} />
        ))}

      <Card bleed>
        <Tabs value={view} onValueChange={(value) => onView(value as ClientView)}>
          <div className="px-5 pt-4">
            <TabsList>
              <TabsTrigger value="boletos">Boletos ({boletos.length})</TabsTrigger>
              <TabsTrigger value="atendimentos">
                Atendimentos ({records.length + tickets.length})
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="boletos" className="pt-2">
            <VehicleBoletos vehicle={vehicle} boletos={boletos} />
          </TabsContent>
          <TabsContent value="atendimentos" className="px-5 pb-5 pt-4">
            <Attendances records={records} tickets={tickets} vehicle={vehicle} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

/**
 * O CARRO aberto: o modelo e o ano num chip e, embaixo, a faixa dos números
 * dele — mensalidade (o último boleto que o cobre, a mesma régua da
 * Carteira), plano, dia de vencimento, contrato e atendimentos.
 */
function VehicleHeader({
  vehicle,
  monthly,
  attendances,
}: {
  vehicle: Vehicle;
  monthly: string | null;
  attendances: number;
}) {
  const model = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
  return (
    <section className={cardClasses({ padding: false, className: 'overflow-hidden' })}>
      <div className="px-5 py-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-superficie-suave px-3 py-1 text-sm">
          <Icon nome="veiculo" size={16} className="text-texto-suave" />
          <span className="font-semibold text-texto">
            {model || 'Modelo não informado'}
          </span>
          {vehicle.modelYear && (
            <span className="text-texto-suave">· {vehicle.modelYear}</span>
          )}
        </span>
      </div>
      <dl className="flex flex-wrap divide-x divide-borda-suave border-t border-borda-suave bg-superficie-suave">
        <Strip label="Mensalidade">
          {monthly === null ? (
            '—'
          ) : (
            <span className="text-sucesso">
              <MoneyValue value={monthly} />
            </span>
          )}
        </Strip>
        <Strip label="Plano">{vehicle.plan ?? '—'}</Strip>
        <Strip label="Vencimento">{vehicle.dueDay ? `Dia ${vehicle.dueDay}` : '—'}</Strip>
        <Strip label="Contrato desde">
          {vehicle.contractDate ? formatDate(vehicle.contractDate) : '—'}
        </Strip>
        <Strip label="Atendimentos">
          {attendances === 1 ? '1 ocorrência' : `${attendances} ocorrências`}
        </Strip>
      </dl>
    </section>
  );
}

/** Um número da faixa: o rótulo pequeno em cima, o valor embaixo. */
function Strip({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: 'alerta';
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 px-5 py-3">
      <dt
        className={cn('rotulo', tone === 'alerta' ? 'text-alerta' : 'text-texto-suave')}
      >
        {label}
      </dt>
      <dd className="text-sm font-bold text-texto">{children}</dd>
    </div>
  );
}

/**
 * O SINISTRO em tratativa no carro: quando entrou, quando entrou na fase, a
 * fase, há quanto tempo está nela — com o selo de vencido quando o SLA da
 * passagem passou (`deadline_at`, gravado na entrada da fase) — e o histórico
 * de fases, da atual para trás. Sem link para o sinistro: o consultor não tem
 * `claims.*`; o que a ficha responde é "em que pé está".
 */
function ClaimCard({ claim }: { claim: Claim }) {
  const now = Date.now();
  const days = (from: string) =>
    Math.max(0, Math.floor((now - new Date(from).getTime()) / 86_400_000));
  const overdue =
    claim.deadlineAt !== null && new Date(claim.deadlineAt).getTime() < now
      ? days(claim.deadlineAt)
      : null;

  return (
    <section
      className={cardClasses({
        tone: 'alerta',
        padding: false,
        className: 'overflow-hidden',
      })}
    >
      <h3 className="flex items-center gap-2 px-5 py-3 text-sm font-bold text-texto">
        <Icon nome="atencao" size={16} className="text-alerta" />
        Em tratativa com o Dept. Sinistro
      </h3>
      <dl className="flex flex-wrap divide-x divide-alerta/20 border-y border-alerta/20">
        <Strip label="Entrada do sinistro" tone="alerta">
          {formatDate(businessDate(new Date(claim.openedAt)))}
        </Strip>
        <Strip label="Início da fase" tone="alerta">
          {claim.phaseSince ? formatDate(businessDate(new Date(claim.phaseSince))) : '—'}
        </Strip>
        <Strip label="Fase atual" tone="alerta">
          {claim.phase}
        </Strip>
        <Strip label="Tempo na fase" tone="alerta">
          {claim.phaseSince === null ? (
            '—'
          ) : (
            <span className="flex items-center gap-2">
              {days(claim.phaseSince) === 1 ? '1 dia' : `${days(claim.phaseSince)} dias`}
              {overdue !== null && (
                <Badge tone="erro">
                  {overdue === 0
                    ? 'Vencido hoje'
                    : `Vencido há ${overdue} ${overdue === 1 ? 'dia' : 'dias'}`}
                </Badge>
              )}
            </span>
          )}
        </Strip>
      </dl>
      {claim.phases.length > 0 && (
        <div className="flex flex-col gap-2 px-5 py-4">
          <span className="rotulo text-alerta">Histórico de fases</span>
          <ol className="relative flex flex-col gap-3 border-l border-borda pl-5">
            {claim.phases.map((p, i) => (
              <li key={i} className="relative flex flex-col text-sm">
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute -left-[1.6rem] top-1.5 size-2.5 rounded-full',
                    i === 0 ? 'bg-primaria' : 'bg-borda-forte',
                  )}
                />
                <span
                  className={cn(
                    i === 0 ? 'font-semibold text-texto' : 'text-texto-suave',
                  )}
                >
                  {p.phase}
                  {i === 0 && (
                    <span className="ml-2">
                      <Badge tone="alerta">Atual</Badge>
                    </span>
                  )}
                </span>
                <span className="text-xs text-texto-suave">
                  {formatDate(businessDate(new Date(p.enteredAt)))}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

/**
 * Os boletos do carro: vencimento original e o atual (diferem quando alguém
 * prorrogou), o pagamento, o valor, o que foi pago e a situação. "Pago" é da
 * tabela de situações; a situação que o espelho não conhece sai como veio.
 */
function VehicleBoletos({ vehicle, boletos }: { vehicle: Vehicle; boletos: Boleto[] }) {
  const date = (iso: string | null) => (iso ? formatDate(iso) : '—');
  return (
    <Table
      label={`Boletos de ${vehicle.plate ?? 'veículo sem placa'}`}
      items={boletos}
      rowKey={(b) => b.code}
      empty="Nenhum boleto deste veículo nos últimos doze meses."
      columns={[
        { title: 'Venc. original', minWidth: '7rem', cell: (b) => date(b.dueOriginal) },
        { title: 'Vencimento', minWidth: '7rem', cell: (b) => date(b.dueDate) },
        {
          title: 'Dt. pagamento',
          minWidth: '7rem',
          cell: (b) => (b.paid === true ? date(b.paidOn) : '—'),
        },
        {
          title: 'Valor',
          alignment: 'fim' as const,
          minWidth: '7rem',
          cell: (b) => (
            <span className="font-semibold">
              <MoneyValue value={b.amount} />
            </span>
          ),
        },
        {
          title: 'Valor pago',
          alignment: 'fim' as const,
          minWidth: '7rem',
          cell: (b) => (b.paid === true ? <MoneyValue value={b.amountPaid} /> : '—'),
        },
        {
          title: 'Status',
          minWidth: '8rem',
          cell: (b) => (
            <Badge
              tone={b.paid === true ? 'sucesso' : b.paid === false ? 'alerta' : 'neutro'}
            >
              {b.paid === true ? 'Pago' : (b.status ?? 'Situação desconhecida')}
            </Badge>
          ),
        },
      ]}
    />
  );
}

/**
 * Os veículos do cliente — Placa | Modelo | Plano | Situação | FIPE | Valor
 * protegido | Contrato | Atendimentos. A linha abre o carro e os boletos —
 * desde 22/10/2026 o clique em qualquer ponto dela, e não só na placa, que
 * continua botão para o teclado. O número de atendimentos é só leitura: quem
 * quer a lista abre o carro, ou "Todos os atendimentos" no topo. Ele soma os do
 * SGA e as demandas que o associado abriu no portal, que dividem a mesma aba.
 * O carro em tratativa no sinistro leva o selo ao lado da placa — é o mesmo
 * destaque da linha da agenda, dito por carro.
 */
function VehiclesCard({
  measured,
  loading,
  selected,
  onOpen,
}: {
  measured: MeasuredClient | undefined;
  loading: boolean;
  selected: string | undefined;
  onOpen: (vehicle: string | undefined, view: ClientView) => void;
}) {
  const tickets = measured?.tickets ?? [];
  const total = (measured?.serviceRecords.length ?? 0) + tickets.length;
  const attendancesOf = (v: Vehicle) =>
    v.serviceRecords + tickets.filter((t) => t.vehicleCode === v.code).length;
  const all = measured?.vehicles ?? [];
  const inClaim = new Set((measured?.claims ?? []).map((c) => c.vehicleCode));
  return (
    <Card
      title={
        measured === undefined
          ? 'Veículos'
          : `${all.length} ${all.length === 1 ? 'veículo' : 'veículos'}`
      }
      descricao={
        measured && (
          <>
            A linha abre o carro, com os boletos e os atendimentos dele. Cadastro do ERP
            de <Age iso={measured.syncedAt} />.
          </>
        )
      }
      actions={
        measured &&
        all.length > 1 &&
        total > 0 && (
          <button
            type="button"
            onClick={() => onOpen(undefined, 'atendimentos')}
            className={linkTextClasses('text-sm')}
          >
            Todos os atendimentos ({total})
          </button>
        )
      }
      bleed
    >
      <Table
        paginate={false}
        label="Veículos do associado"
        items={all}
        loading={loading}
        skeletonRows={1}
        rowKey={(v) => v.code}
        onRowClick={(v) => onOpen(v.code, 'boletos')}
        empty="Nenhum veículo deste associado com este consultor."
        columns={[
          {
            title: 'Placa',
            minWidth: '7rem',
            cell: (v) => (
              <span className="flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => onOpen(v.code, 'boletos')}
                  aria-pressed={v.code === selected}
                  className={linkTextClasses(
                    'font-mono font-medium aria-pressed:underline',
                  )}
                >
                  {v.plate ?? 'sem placa'}
                </button>
                {inClaim.has(v.code) && (
                  <Badge tone="alerta" className="whitespace-nowrap">
                    em sinistro
                  </Badge>
                )}
              </span>
            ),
          },
          {
            title: 'Modelo',
            alignment: 'inicio' as const,
            minWidth: '14rem',
            cell: (v) => <ModelCell vehicle={v} />,
          },
          {
            title: 'Plano',
            minWidth: '11rem',
            cell: (v) => <PlanCell vehicle={v} />,
          },
          {
            title: 'Situação',
            minWidth: '8rem',
            cell: (v) => <VehicleStatus vehicle={v} />,
          },
          {
            title: 'FIPE',
            alignment: 'fim' as const,
            minWidth: '7.5rem',
            cell: (v) => <MoneyValue value={v.fipeValue} />,
          },
          {
            title: 'Valor protegido',
            alignment: 'fim' as const,
            minWidth: '7.5rem',
            cell: (v) => <MoneyValue value={v.protectedValue} />,
          },
          {
            title: 'Contrato',
            minWidth: '6.5rem',
            cell: (v) =>
              v.contractDate ? (
                formatDate(v.contractDate)
              ) : (
                <NotProvided>sem contrato</NotProvided>
              ),
          },
          {
            title: 'Atendimentos',
            alignment: 'fim' as const,
            minWidth: '7rem',
            cell: (v) => (
              <span
                className={
                  attendancesOf(v) === 0
                    ? 'tabular-nums text-texto-suave'
                    : 'tabular-nums'
                }
              >
                {attendancesOf(v)}
              </span>
            ),
          },
        ]}
      />
    </Card>
  );
}

/**
 * Os quatro números do carro aberto, logo abaixo do título: a situação dele no
 * ERP, o boleto mais recente, e quantos boletos foram pagos e quantos não.
 *
 * Nenhum leva `tone`, de propósito. "Não pago" não é "vencido": sem pagamento
 * é também o boleto que ainda vai vencer e o cancelado, e pintar o número de
 * vermelho seria a tela inventando a dívida — o atraso de verdade é o
 * `delinquent` do boleto, na coluna "Em atraso" da Carteira (`daysLate`, em
 * `AgendaScreen.tsx`). A situação do carro também fica cinza —
 * a casa não classifica a situação do veículo do ERP.
 *
 * A conta é do recorte de doze meses que a ficha lê, e diz isso: "3 não pagos"
 * sem a janela seria lido como a dívida inteira do carro.
 *
 * Sem `vehicle`, é o esqueleto da página que ainda não respondeu.
 */
function VehicleSummary({
  vehicle,
  boletos = [],
  dueFrom,
  highlighted = false,
  onHighlight,
}: {
  vehicle?: Vehicle;
  boletos?: Boleto[];
  dueFrom?: string;
  /** Os não pagos estão destacados na tabela de boletos. */
  highlighted?: boolean;
  /** Liga e desliga o destaque dos não pagos. */
  onHighlight?: () => void;
}) {
  const known = vehicle !== undefined;
  const paid = boletos.filter((b) => b.paid === true).length;
  const open = boletos.filter((b) => b.paid === false);
  const unknown = boletos.length - paid - open.length;
  /* A lista chega do vencimento mais novo ao mais antigo. */
  const latest = boletos[0];
  const oldestOpen = open[open.length - 1];
  const description = vehicle
    ? [vehicle.brand, vehicle.model].filter(Boolean).join(' ')
    : '';

  return (
    <StatCardList stretch>
      <StatCard
        icon="veiculo"
        label={
          vehicle ? `Situação de ${vehicle.plate ?? 'veículo sem placa'}` : 'Situação'
        }
        value={known ? <VehicleStatus vehicle={vehicle} /> : undefined}
        support={
          vehicle && (
            <span className="block truncate" title={description}>
              {description || 'modelo não informado'}
            </span>
          )
        }
      />
      <StatCard
        icon="codigo-barras"
        label="Boleto mais recente"
        value={
          !known ? undefined : latest ? <MoneyValue value={latest.amount} /> : 'nenhum'
        }
        support={
          known &&
          (latest
            ? `${latest.dueDate ? `vence ${formatDate(latest.dueDate)}` : 'sem vencimento'} · ${paidWord(latest)}`
            : 'nos últimos doze meses')
        }
      />
      <StatCard
        icon="confirmado"
        label="Boletos pagos"
        value={
          known ? (
            <span className="tabular-nums">
              {paid}{' '}
              <span className="text-sm font-medium text-texto-suave">
                de {boletos.length}
              </span>
            </span>
          ) : undefined
        }
        support={dueFrom && `vencimento desde ${formatDate(dueFrom)}`}
      />
      <StatCard
        icon="cobranca"
        label="Não pagos"
        value={known ? <span className="tabular-nums">{open.length}</span> : undefined}
        onClick={known && open.length > 0 ? onHighlight : undefined}
        pressed={highlighted}
        support={
          known &&
          (oldestOpen?.dueDate
            ? `vencimento mais antigo ${formatDate(oldestOpen.dueDate)}`
            : 'nenhum no recorte')
        }
        footer={
          unknown > 0
            ? `${unknown} ${unknown === 1 ? 'boleto' : 'boletos'} com situação que o ERP não classifica.`
            : undefined
        }
      />
    </StatCardList>
  );
}

/**
 * O mesmo aviso, quando nenhum carro está aberto: quais placas estão em
 * tratativa, cada uma abrindo o próprio carro — onde a fase e o tempo moram.
 */
function ClaimsAlert({
  claims,
  vehicles,
  onOpen,
}: {
  claims: Claim[];
  vehicles: Vehicle[];
  onOpen: (vehicle: string) => void;
}) {
  const inClaim = vehicles.filter((v) => claims.some((c) => c.vehicleCode === v.code));
  if (inClaim.length === 0) return null;
  return (
    <NoticeBanner
      eyebrow="Atendimento com o Dept. Sinistro"
      title={
        <>
          {inClaim.length === 1 ? 'O veículo ' : 'Os veículos '}
          {inClaim.map((v, i) => (
            <span key={v.code}>
              {i > 0 && (i === inClaim.length - 1 ? ' e ' : ', ')}
              <button
                type="button"
                onClick={() => onOpen(v.code)}
                className={linkTextClasses('font-mono')}
              >
                {v.plate ?? 'sem placa'}
              </button>
            </span>
          ))}
          {inClaim.length === 1 ? ' está' : ' estão'} em tratativa com o sinistro.
        </>
      }
    >
      <p className="text-sm text-texto-suave">
        A placa abre o carro, com a fase atual e há quanto tempo ele está nela.
      </p>
    </NoticeBanner>
  );
}

/**
 * Uma linha da aba de atendimentos: o que o associado abriu no portal ou o que
 * a operação registrou no SGA. Para quem lê a agenda é a MESMA coisa — o
 * cliente procurou a casa —, e a origem é um detalhe da linha.
 */
type Attendance =
  | {
      kind: 'demanda';
      key: string;
      date: string;
      plate: string | null;
      ticket: MemberTicket;
    }
  | {
      kind: 'sga';
      key: string;
      date: string | null;
      plate: string | null;
      record: ServiceRecord;
    };

/**
 * As duas listas numa linha do tempo só, da mais nova à mais antiga. A
 * demanda conta pelo DIA de abertura, que é a mesma régua do atendimento do
 * SGA (data de negócio); o atendimento sem data vai para o fim.
 */
function attendancesOf(records: ServiceRecord[], tickets: MemberTicket[]): Attendance[] {
  const all: Attendance[] = [
    ...tickets.map((t) => ({
      kind: 'demanda' as const,
      key: `demanda-${t.code}`,
      date: businessDate(new Date(t.openedAt)),
      plate: t.plate,
      ticket: t,
    })),
    ...records.map((r) => ({
      kind: 'sga' as const,
      key: `sga-${r.code}`,
      date: r.registeredOn,
      plate: r.plate,
      record: r,
    })),
  ];
  /* `sort` é estável: no mesmo dia, a demanda (mais nova por instante) vem antes */
  return all.sort((a, b) => {
    if (a.date === b.date) return 0;
    if (a.date === null) return 1;
    if (b.date === null) return -1;
    return a.date < b.date ? 1 : -1;
  });
}

/**
 * A aba de atendimentos (22/10/2026): UMA tabela, com as demandas que o
 * associado abriu no portal e os atendimentos do SGA misturados por data. Eram
 * dois cartões, e quem lê a agenda perguntava por que "demanda" e
 * "atendimento" estavam separados — para o consultor, os dois são o cliente
 * procurando a casa. A origem continua dita, na linha.
 *
 * O cartão não repete a contagem, que a aba já mostra; a descrição só aparece
 * quando algum atendimento saiu do histórico do SGA.
 *
 * Tabela: Placa (sem carro aberto) | Atendimento, resumido. A demanda leva a
 * situação ao lado do título, porque é a única que tem uma; o atendimento do
 * SGA leva as primeiras linhas da descrição — o de sinistro tem trinta, e a
 * tabela inteira virava um atendimento. Só o resumo da demanda, e não a
 * conversa, como no Extrato: ela pode ter nota interna. O número abre a
 * demanda na mesa só para quem tem a permissão de abri-la; o atendimento do SGA
 * não abre — a página é do ERP, e o consultor não tem `erp.*`.
 */
function Attendances({
  records,
  tickets,
  vehicle,
}: {
  records: ServiceRecord[];
  tickets: MemberTicket[];
  vehicle: Vehicle | undefined;
}) {
  const items = attendancesOf(records, tickets);
  const missing = records.filter((r) => r.missing).length;

  /*
   * Sem resumo de contagem desde 29/10/2026 (pedido do dono): a quantidade já
   * está na aba. Fica só o aviso do que saiu do SGA, que nenhum outro lugar diz.
   */
  const summary =
    items.length === 0
      ? 'O que o associado abriu no portal e o que a operação registrou no SGA.'
      : missing > 0
        ? `${missing} ${missing === 1 ? 'saiu' : 'saíram'} do histórico do SGA.`
        : undefined;

  return (
    <Card
      title={
        vehicle === undefined
          ? 'Atendimentos do associado'
          : `Atendimentos de ${vehicle.plate ?? 'veículo sem placa'}`
      }
      descricao={summary}
      bleed
    >
      <Table
        label="Atendimentos"
        items={items}
        rowKey={(a) => a.key}
        empty={
          vehicle === undefined
            ? 'Nenhum atendimento nos veículos deste associado com este consultor.'
            : 'Nenhum atendimento neste veículo.'
        }
        columns={[
          ...(vehicle === undefined
            ? [
                {
                  title: 'Placa',
                  minWidth: '6.5rem',
                  cell: (a: Attendance) =>
                    a.plate ? (
                      <span className="font-mono">{a.plate}</span>
                    ) : (
                      <NotProvided>sem placa</NotProvided>
                    ),
                },
              ]
            : []),
          {
            title: 'Atendimento',
            alignment: 'inicio' as const,
            minWidth: '20rem',
            cell: (a: Attendance) =>
              a.kind === 'demanda' ? (
                <TicketSummary ticket={a.ticket} />
              ) : (
                <ServiceRecordSummary record={a.record} />
              ),
          },
        ]}
      />
    </Card>
  );
}

function TicketSummary({ ticket }: { ticket: MemberTicket }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-texto">{ticket.title}</span>
        <Badge tone={ticketStatusTone(ticket.status)}>
          {ticketStatusLabel(ticket.status)}
        </Badge>
      </span>
      <span className="text-xs text-texto-suave">
        {formatDate(businessDate(new Date(ticket.openedAt)))} · aberto pelo associado no
        portal · ticket{' '}
        {ticket.link ? (
          <a href={ticket.link} className={linkTextClasses('text-xs')}>
            {ticket.code}
          </a>
        ) : (
          ticket.code
        )}
        {ticket.category && ` · ${ticket.category}`}
        {ticket.completedAt &&
          ` · concluída em ${formatDate(businessDate(new Date(ticket.completedAt)))}`}
      </span>
    </span>
  );
}

function ServiceRecordSummary({ record }: { record: ServiceRecord }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="font-medium text-texto">
        {record.title ?? <NotProvided>sem título</NotProvided>}
      </span>
      <span className="text-xs text-texto-suave">
        {record.registeredOn ? formatDate(record.registeredOn) : 'sem data'} · registrado
        no SGA · atendimento {record.code}
        {record.missing && ' · saiu do histórico do SGA'}
      </span>
      {record.description && (
        <span className="mt-0.5 line-clamp-2 whitespace-pre-line text-sm text-texto-suave">
          {plainText(record.description)}
        </span>
      )}
    </span>
  );
}

function yearOf(vehicle: Vehicle): string | null {
  return vehicle.manufactureYear &&
    vehicle.modelYear &&
    vehicle.manufactureYear !== vehicle.modelYear
    ? `${vehicle.manufactureYear}/${vehicle.modelYear}`
    : (vehicle.modelYear ?? vehicle.manufactureYear);
}

/** A situação do carro como o ERP a chama — sem classificação da casa. */
function VehicleStatus({ vehicle }: { vehicle: Vehicle }) {
  return (
    vehicle.status.description ?? (
      <NotProvided>
        {vehicle.status.code === null ? 'não informada' : `código ${vehicle.status.code}`}
      </NotProvided>
    )
  );
}

/** Marca e modelo em cima; o ano embaixo. */
function ModelCell({ vehicle }: { vehicle: Vehicle }) {
  const description = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
  const year = yearOf(vehicle);
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-texto">
        {description || <NotProvided>modelo não informado</NotProvided>}
      </span>
      {year && <span className="text-xs text-texto-suave">{year}</span>}
    </span>
  );
}

/**
 * A categoria do SGA, que a casa lê como plano, e o grupo de produto embaixo.
 *
 * O SGA não tem um campo "plano": a `categoria` diz PLANO PRATA, OURO, PADRÃO
 * ou BRONZE em ~81% dos carros da CE (29/09/2026), e PASSEIO ou MOTORISTA DE
 * APLICATIVO no resto. A coluna mostra os dois em vez de escolher um.
 */
function PlanCell({ vehicle }: { vehicle: Vehicle }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-texto">
        {vehicle.category ?? <NotProvided>não informada</NotProvided>}
      </span>
      {vehicle.plan !== null && (
        <span className="truncate text-xs text-texto-suave">{vehicle.plan}</span>
      )}
    </span>
  );
}

/** "pago", "não pago" ou "não sabemos" — só a tabela de situações diz. */
function paidWord(boleto: Boleto): string {
  return boleto.paid === null
    ? 'não sabemos se foi pago'
    : boleto.paid
      ? 'pago'
      : 'não pago';
}
